import {
  getMostRecentPayment,
  getPaymentMethodSummary,
  isCardPayment,
  type PaymentOnPurchase
} from '|/services/purchase-payments';

export const CARD_PAYMENT_POLL_INTERVAL_MS = 2000;
export const CARD_PAYMENT_POLL_TIMEOUT_MS = 30000;
export const PAYMENT_SUMMARY_REQUEST_TIMEOUT_MS = 5000;

export type CardPaymentResolution =
  | {
      status: 'Succeeded' | 'Failed';
      payment: PaymentOnPurchase;
      pollAttempts: number;
      secondsToResolve: number;
    }
  | { status: 'timed_out'; pollAttempts: number; secondsToResolve: number };

const toAbortError = (signal: AbortSignal) =>
  signal.reason instanceof Error ? signal.reason : new Error('Aborted');

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(toAbortError(signal));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });

// The summary GET has no timeout of its own, so a stalled request would hold Place Order
// in its loading state. Each request gets its own controller, aborted by the caller's
// signal or after PAYMENT_SUMMARY_REQUEST_TIMEOUT_MS, whichever comes first.
const getBoundedPaymentMethodSummary = async (signal: AbortSignal) => {
  const controller = new AbortController();
  const abortWithCaller = () => {
    controller.abort(toAbortError(signal));
  };
  const timer = setTimeout(() => {
    controller.abort(new Error('Payment summary request timed out'));
  }, PAYMENT_SUMMARY_REQUEST_TIMEOUT_MS);
  signal.addEventListener('abort', abortWithCaller, { once: true });

  try {
    return await getPaymentMethodSummary(controller.signal);
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', abortWithCaller);
  }
};

export const getCardPaymentIds = async (signal: AbortSignal) => {
  const { paymentsOnPurchase } = await getBoundedPaymentMethodSummary(signal);
  return new Set(paymentsOnPurchase.filter(isCardPayment).map(({ paymentId }) => paymentId));
};

export const getNewFailedPayment = async (
  previousCardPaymentIds: ReadonlySet<string> | null,
  signal: AbortSignal
) => {
  const { paymentsOnPurchase } = await getBoundedPaymentMethodSummary(signal);
  return getMostRecentPayment(
    paymentsOnPurchase,
    payment => payment.status === 'Failed' && !previousCardPaymentIds?.has(payment.paymentId)
  );
};

// collect-payment can return before the card charge settles, so the outcome only shows up
// later on the payment-method summary. Payments that existed before the charge are ignored
// so an earlier decline can't be mistaken for this one.
export const waitForCardPaymentResolution = async (
  previousCardPaymentIds: ReadonlySet<string>,
  signal: AbortSignal
): Promise<CardPaymentResolution> => {
  const startedAt = Date.now();
  const deadline = startedAt + CARD_PAYMENT_POLL_TIMEOUT_MS;
  const getSecondsElapsed = () => Math.round((Date.now() - startedAt) / 100) / 10;
  let pollAttempts = 0;

  while (Date.now() < deadline) {
    await wait(Math.min(CARD_PAYMENT_POLL_INTERVAL_MS, deadline - Date.now()), signal);
    pollAttempts += 1;

    try {
      const { paymentsOnPurchase } = await getBoundedPaymentMethodSummary(signal);
      const payment = getMostRecentPayment(
        paymentsOnPurchase,
        candidate => isCardPayment(candidate) && !previousCardPaymentIds.has(candidate.paymentId)
      );

      if (payment?.status === 'Succeeded' || payment?.status === 'Failed') {
        return {
          status: payment.status,
          payment,
          pollAttempts,
          secondsToResolve: getSecondsElapsed()
        };
      }
    } catch (error) {
      if (signal.aborted) throw error;
    }
  }

  return { status: 'timed_out', pollAttempts, secondsToResolve: getSecondsElapsed() };
};
